import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReviEvaluationsApi } from "../../../../../api/revi/ReviEvaluationsApi";

function hasRealQuestions(ev) {
  if (!ev || !Array.isArray(ev.questions)) return false;
  return ev.questions.some(
    (q) => typeof q?.text === "string" && q.text.trim().length > 0,
  );
}

function dedupeById(items) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const id = item?._id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}

export function useSessionEvaluationPicker({
  accessToken,
  therapistId,
  sceneEvaluations,
  notify,
}) {
  const evalApi = useMemo(() => new ReviEvaluationsApi(), []);

  const [source, setSource] = useState("indicated");

  const [myEvals, setMyEvals] = useState([]);
  const [generalEvals, setGeneralEvals] = useState([]);

  const [loadedMine, setLoadedMine] = useState(false);
  const [loadedGeneral, setLoadedGeneral] = useState(false);

  const [loadingMine, setLoadingMine] = useState(false);
  const [loadingGeneral, setLoadingGeneral] = useState(false);

  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [loadingPickedDetail, setLoadingPickedDetail] = useState(false);

  const hydrateReqIdRef = useRef(0);

  const sceneList = useMemo(
    () => (Array.isArray(sceneEvaluations) ? sceneEvaluations : []),
    [sceneEvaluations],
  );

  const loadMine = useCallback(async () => {
    if (!accessToken || !therapistId) return;
    setLoadingMine(true);
    try {
      const { evaluations } = await evalApi.getMyEvaluations(
        accessToken,
        therapistId,
        { page: 1, limit: 50 },
      );
      setMyEvals(evaluations || []);
      setLoadedMine(true);
    } catch (e) {
      notify?.error?.(e?.message || "No se pudieron cargar tus evaluaciones");
      setMyEvals([]);
    } finally {
      setLoadingMine(false);
    }
  }, [accessToken, therapistId, evalApi, notify]);

  // Carga eager al montar: "Indicadas" es el tab por defecto y mezcla
  // los tests de la sala con los propios del terapeuta.
  useEffect(() => {
    if (!accessToken || !therapistId) return;
    if (loadedMine || loadingMine) return;
    loadMine();
  }, [accessToken, therapistId, loadedMine, loadingMine, loadMine]);

  const loadGeneral = useCallback(async () => {
    if (!accessToken) return;
    setLoadingGeneral(true);
    try {
      const [standardRes, sharedRes] = await Promise.all([
        evalApi.getCatalog(accessToken, {
          page: 1,
          limit: 50,
          type: "standardized",
        }),
        evalApi
          .getSharedEvaluations(accessToken, { page: 1, limit: 50 })
          .catch(() => ({ evaluations: [] })),
      ]);
      const merged = dedupeById([
        ...(standardRes?.evaluations || []),
        ...(sharedRes?.evaluations || []),
      ]);
      setGeneralEvals(merged);
      setLoadedGeneral(true);
    } catch (e) {
      notify?.error?.(e?.message || "No se pudo cargar el catálogo general");
      setGeneralEvals([]);
    } finally {
      setLoadingGeneral(false);
    }
  }, [accessToken, evalApi, notify]);

  const changeSource = useCallback(
    (next) => {
      setSource(next);
      setSelectedEvaluationId(null);
      setPickedTemplate(null);
      if (next === "general" && !loadedGeneral && !loadingGeneral)
        loadGeneral();
    },
    [loadedGeneral, loadingGeneral, loadGeneral],
  );

  // "Indicadas" = tests de la sala + propias del terapeuta (deduped)
  const indicatedList = useMemo(
    () => dedupeById([...sceneList, ...myEvals]),
    [sceneList, myEvals],
  );

  const currentList = useMemo(() => {
    if (source === "general") return generalEvals;
    return indicatedList;
  }, [source, indicatedList, generalEvals]);

  const pickEvaluation = useCallback(
    async (id) => {
      setSelectedEvaluationId(id);
      if (!id) {
        setPickedTemplate(null);
        return;
      }

      const fromList = (currentList || []).find((e) => e._id === id) || null;

      if (fromList && hasRealQuestions(fromList)) {
        setPickedTemplate(fromList);
        return;
      }

      if (!accessToken) {
        setPickedTemplate(fromList);
        return;
      }

      const reqId = ++hydrateReqIdRef.current;
      setPickedTemplate(fromList);
      setLoadingPickedDetail(true);
      try {
        const full = await evalApi.getEvaluationById(accessToken, id);
        if (reqId !== hydrateReqIdRef.current) return;
        setPickedTemplate(full || fromList);
      } catch (e) {
        if (reqId !== hydrateReqIdRef.current) return;
        notify?.error?.(e?.message || "No se pudo cargar el detalle");
      } finally {
        if (reqId === hydrateReqIdRef.current) setLoadingPickedDetail(false);
      }
    },
    [accessToken, currentList, evalApi, notify],
  );

  const reset = useCallback(() => {
    hydrateReqIdRef.current += 1;
    setSelectedEvaluationId(null);
    setPickedTemplate(null);
    setLoadingPickedDetail(false);
  }, []);

  useEffect(() => {
    if (source === "indicated") reset();
  }, [sceneList, source, reset]);

  return {
    source,
    setSource: changeSource,

    currentList,
    loadingList:
      (source === "indicated" && loadingMine) ||
      (source === "general" && loadingGeneral),

    indicatedCount: indicatedList.length,

    selectedEvaluationId,
    pickEvaluation,
    pickedTemplate,
    loadingPickedDetail,

    reset,
  };
}
